﻿package br.com.htms.inspeccaocampo.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.AppConfig
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.decodeToImageBitmap
import br.com.htms.inspeccaocampo.platform.platformName
import br.com.htms.inspeccaocampo.shared.ui.components.ButtonLoadingContent
import br.com.htms.inspeccaocampo.shared.ui.components.CampoFotoRow
import br.com.htms.inspeccaocampo.shared.ui.components.CampoSimNaoCard
import br.com.htms.inspeccaocampo.shared.ui.components.CenteredAppDialog
import br.com.htms.inspeccaocampo.shared.ui.components.ChecklistProgressBar
import br.com.htms.inspeccaocampo.shared.ui.components.FormFieldDefaults
import br.com.htms.inspeccaocampo.shared.ui.components.ItemVerificacaoCard
import br.com.htms.inspeccaocampo.shared.ui.components.ManualDialog
import br.com.htms.inspeccaocampo.shared.ui.components.SignatureField
import br.com.htms.inspeccaocampo.shared.ui.components.SignaturePadScreen
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SharedFormScreen(
    controller: AppController,
    embedded: Boolean = false
) {
    val form by controller.form.collectAsState()
    val erros by controller.erros.collectAsState()
    val capturandoGps by controller.capturandoGps.collectAsState()
    val modoTreinamento by controller.modoTreinamento.collectAsState()
    val appConfig by controller.appConfig.collectAsState()
    val assinaturaEditando by controller.assinaturaEditando.collectAsState()
    val mensagem by controller.mensagem.collectAsState()
    val idEdicao by controller.idEdicao.collectAsState()
    val obrasAtivas by controller.obrasAtivas.collectAsState()

    val snackbar = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val listState = rememberLazyListState()

    var mostrarManual by remember { mutableStateOf(false) }
    var confirmarNova by remember { mutableStateOf(false) }
    var menuObraAberto by remember { mutableStateOf(false) }
    var mostrarListaInfoAdicionais by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        controller.carregarObrasAtivas()
    }

    val verificacoesRespondidas = form.itens.count { it.resposta != null }
    val totalVerificacoes = form.itens.size

    fun scrollSeguro(indice: Int) {
        scope.launch {
            val total = listState.layoutInfo.totalItemsCount
            if (total <= 0) return@launch
            val alvo = indice.coerceIn(0, total - 1)
            runCatching { listState.animateScrollToItem(alvo) }
        }
    }

    if (!embedded) {
        LaunchedEffect(mensagem) {
            val msg = mensagem ?: return@LaunchedEffect
            snackbar.showSnackbar(msg)
            controller.consumirMensagem()
        }
    }

    LaunchedEffect(erros) {
        if (erros.isEmpty()) return@LaunchedEffect
        val primeiro = erros.first()
        val idx = indiceLazyDoCampo(
            campo = primeiro.campo,
            modoTreinamento = modoTreinamento,
            idEdicao = idEdicao,
            itemIds = form.itens.map { it.id }
        )
        val total = listState.layoutInfo.totalItemsCount
        if (total > 0) {
            runCatching { listState.animateScrollToItem(idx.coerceIn(0, total - 1)) }
        }
    }

    if (assinaturaEditando != null) {
        val campo = assinaturaEditando!!
        val titulo = if (campo == "supervisor") "Assinatura — SUPERVISOR" else "Assinatura — SESMT"
        SignaturePadScreen(
            titulo = titulo,
            onCancelar = { controller.fecharAssinatura() },
            onConfirmar = { token -> controller.salvarAssinatura(campo, token) }
        )
        return
    }

    if (mostrarManual) ManualDialog(onDismiss = { mostrarManual = false })

    if (confirmarNova) {
        CenteredAppDialog(
            onDismissRequest = { confirmarNova = false },
            title = "Nova inspeção",
            text = "Descartar o preenchimento atual e iniciar uma nova inspeção?",
            confirmLabel = "Sim",
            onConfirm = {
                controller.iniciarNovaInspecao()
                confirmarNova = false
            },
            dismissLabel = "Cancelar"
        )
    }

    val content: @Composable (PaddingValues) -> Unit = { padding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .background(Color.Transparent)
        ) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(bottom = 96.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                item { SharedFormHeader(appConfig) }

                if (modoTreinamento) {
                    item {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            colors = CardDefaults.cardColors(containerColor = Color(0xFFFFF3E0))
                        ) {
                            Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                Text("Modo treinamento ativo", fontWeight = FontWeight.Bold)
                                Text("Inspeção de exemplo. Não será salva no histórico real.")
                                OutlinedButton(
                                    onClick = { controller.desativarTreinamento() },
                                    modifier = Modifier.fillMaxWidth()
                                ) { Text("Sair do modo treinamento") }
                            }
                        }
                    }
                }

                item {
                    Row(
                        Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        OutlinedButton(onClick = { mostrarManual = true }, modifier = Modifier.weight(1f)) {
                            Text("Ver manual")
                        }
                        OutlinedButton(onClick = { confirmarNova = true }, modifier = Modifier.weight(1f)) {
                            Text("Nova inspeção")
                        }
                    }
                }

                item {
                    OutlinedButton(
                        onClick = { controller.openNrVigente() },
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
                    ) { Text("NR Vigente") }
                }

                if (idEdicao != null) {
                    item {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.secondaryContainer
                            )
                        ) {
                            Text(
                                "Editando inspeção salva (#$idEdicao)",
                                modifier = Modifier.padding(12.dp),
                                fontWeight = FontWeight.Medium
                            )
                        }
                    }
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
                    ) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            Text("Dados gerais", fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = form.tstResponsavel,
                                onValueChange = { v -> controller.atualizar { it.copy(tstResponsavel = v) } },
                                label = { Text("TST Responsável pelo Preenchimento *") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("tstResponsavel") != null
                            )
                            OutlinedTextField(
                                value = form.dataHorarioFormatado(),
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Data e Horário (automático)") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors()
                            )
                            ExposedDropdownMenuBox(
                                expanded = menuObraAberto,
                                onExpandedChange = { menuObraAberto = it }
                            ) {
                                OutlinedTextField(
                                    value = form.obra.ifBlank { "Selecione a obra" },
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text("Obra *") },
                                    trailingIcon = {
                                        ExposedDropdownMenuDefaults.TrailingIcon(expanded = menuObraAberto)
                                    },
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .menuAnchor(),
                                    colors = FormFieldDefaults.textFieldColors(),
                                    isError = controller.erroNoCampo("obra") != null,
                                    singleLine = true
                                )
                                DropdownMenu(
                                    expanded = menuObraAberto,
                                    onDismissRequest = { menuObraAberto = false }
                                ) {
                                    obrasAtivas.forEach { obraItem ->
                                        DropdownMenuItem(
                                            text = {
                                                Text(
                                                    obraItem.nome,
                                                    maxLines = 2,
                                                    overflow = TextOverflow.Ellipsis
                                                )
                                            },
                                            onClick = {
                                                controller.atualizar { it.copy(obra = obraItem.nome) }
                                                menuObraAberto = false
                                            }
                                        )
                                    }
                                }
                            }
                            OutlinedTextField(
                                value = form.efetivo,
                                onValueChange = { v -> controller.atualizar { it.copy(efetivo = v) } },
                                label = { Text("Efetivo (quantidade de funcionários) *") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("efetivo") != null
                            )
                        }
                    }
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = Color(0xFFB3C9E8))
                    ) {
                        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            Text("Local e Responsável pela Frente *", fontWeight = FontWeight.Bold)
                            OutlinedTextField(
                                value = form.localTexto.ifBlank { "Aguardando GPS..." },
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Local (GPS)") },
                                modifier = Modifier.fillMaxWidth(),
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("localGps") != null
                            )
                            if (form.latitude != null && form.longitude != null) {
                                Text(
                                    "Lat: ${form.latitude}  Lng: ${form.longitude}",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                            OutlinedButton(
                                onClick = { controller.capturarGps() },
                                enabled = !capturandoGps,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                ButtonLoadingContent(
                                    loading = capturandoGps,
                                    loadingText = "Capturando GPS...",
                                    idleText = "Atualizar local por GPS"
                                )
                            }
                            OutlinedTextField(
                                value = form.responsavelFrente,
                                onValueChange = { v -> controller.atualizar { it.copy(responsavelFrente = v) } },
                                label = { Text("Responsável pela Frente da Atividade *") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 2,
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("responsavelFrente") != null
                            )
                            OutlinedTextField(
                                value = form.descricaoAtividade,
                                onValueChange = { v -> controller.atualizar { it.copy(descricaoAtividade = v) } },
                                label = { Text("Descrição da Atividade *") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                colors = FormFieldDefaults.textFieldColors(),
                                isError = controller.erroNoCampo("descricaoAtividade") != null
                            )
                        }
                    }
                }

                item {
                    ChecklistProgressBar(
                        respondidas = verificacoesRespondidas,
                        total = totalVerificacoes,
                        modifier = Modifier.padding(bottom = 4.dp)
                    )
                }

                item {
                    Text(
                        "Verificações de campo *",
                        modifier = Modifier.padding(horizontal = 16.dp),
                        fontWeight = FontWeight.Bold
                    )
                }

                items(form.itens, key = { it.id }) { item ->
                    ItemVerificacaoCard(
                        item = item,
                        erro = controller.erroNoCampo(item.id),
                        onResposta = { controller.definirResposta(item.id, it) },
                        onJustificativa = { txt ->
                            controller.atualizarItem(item.id) { it.copy(justificativa = txt) }
                        },
                        onTirarFoto = { controller.adicionarFotoPlaceholder(item.id) },
                        onAdicionarMaisFoto = { controller.adicionarFotoPlaceholder(item.id) },
                        onRemoverFoto = { foto -> controller.removerFotoItem(item.id, foto) },
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

                item {
                    CampoSimNaoCard(
                        titulo = "Desvios Encontrados *",
                        secao = form.desviosEncontrados,
                        erro = controller.erroNoCampo("desvios"),
                        onResposta = { controller.definirRespostaSecao("desvios", it) },
                        onDescricao = { txt -> controller.atualizarDescricaoSecao("desvios", txt) },
                        onTirarFoto = { },
                        onAdicionarMaisFoto = { },
                        onRemoverFoto = { },
                        descricaoLabel = "Descreva os desvios *",
                        exigirDescricaoSeSim = true,
                        exigirFotoSeSim = false,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

                item {
                    CampoSimNaoCard(
                        titulo = "Ações Corretivas *",
                        secao = form.acoesCorretivas,
                        erro = controller.erroNoCampo("acoesCorretivas"),
                        onResposta = { controller.definirRespostaSecao("acoesCorretivas", it) },
                        onDescricao = { txt -> controller.atualizarDescricaoSecao("acoesCorretivas", txt) },
                        onTirarFoto = { controller.adicionarFotoSecaoPlaceholder("acoesCorretivas") },
                        onAdicionarMaisFoto = { controller.adicionarFotoSecaoPlaceholder("acoesCorretivas") },
                        onRemoverFoto = { foto -> controller.removerFotoSecao("acoesCorretivas", foto) },
                        descricaoLabel = "Descreva as ações corretivas *",
                        exigirDescricaoSeSim = true,
                        exigirFotoSeSim = true,
                        modifier = Modifier.padding(horizontal = 16.dp)
                    )
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
                    ) {
                        Column(
                            Modifier.padding(12.dp),
                            verticalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            OutlinedTextField(
                                value = form.informacoesAdicionais,
                                onValueChange = { v ->
                                    controller.atualizar { it.copy(informacoesAdicionais = v) }
                                },
                                label = { Text("Informações adicionais") },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                colors = FormFieldDefaults.textFieldColors()
                            )
                            CampoFotoRow(
                                label = "Adicionar foto",
                                obrigatorio = false,
                                quantidadeFotos = form.informacoesAdicionaisFotos.size,
                                onTirarFoto = { controller.adicionarFotoInformacoesAdicionais() },
                                onAdicionarMais = { controller.adicionarFotoInformacoesAdicionais() },
                                onAbrirLista = { mostrarListaInfoAdicionais = true }
                            )
                        }
                    }
                }

                item {
                    Card(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
                    ) {
                        OutlinedTextField(
                            value = form.conclusao,
                            onValueChange = { v -> controller.atualizar { it.copy(conclusao = v) } },
                            label = { Text("Conclusão *") },
                            modifier = Modifier.fillMaxWidth().padding(12.dp),
                            minLines = 3,
                            colors = FormFieldDefaults.textFieldColors(),
                            isError = controller.erroNoCampo("conclusao") != null
                        )
                    }
                }

                item {
                    Column(
                        Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        SignatureField(
                            label = "SUPERVISOR",
                            assinaturaToken = form.assinaturaSupervisor,
                            erro = controller.erroNoCampo("assinaturaSupervisor"),
                            onClick = { controller.abrirAssinatura("supervisor") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        SignatureField(
                            label = "SESMT",
                            assinaturaToken = form.assinaturaSesmt,
                            erro = controller.erroNoCampo("assinaturaSesmt"),
                            onClick = { controller.abrirAssinatura("sesmt") },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                if (erros.isNotEmpty()) {
                    item {
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.errorContainer
                            )
                        ) {
                            Column(
                                modifier = Modifier.padding(12.dp),
                                verticalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Text("Corrija os campos obrigatórios:", fontWeight = FontWeight.Bold)
                                Text(
                                    "Toque em um item para ir ao campo.",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onErrorContainer.copy(alpha = 0.8f)
                                )
                                erros.take(5).forEach { erro ->
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .clickable {
                                                val idx = indiceLazyDoCampo(
                                                    campo = erro.campo,
                                                    modoTreinamento = modoTreinamento,
                                                    idEdicao = idEdicao,
                                                    itemIds = form.itens.map { it.id }
                                                )
                                                scope.launch {
                                                    scrollSeguro(idx)
                                                }
                                            }
                                            .padding(vertical = 4.dp)
                                    ) {
                                        Text(
                                            text = "• ${erro.mensagem}",
                                            color = MaterialTheme.colorScheme.onErrorContainer,
                                            textDecoration = TextDecoration.Underline
                                        )
                                    }
                                }
                                if (erros.size > 5) {
                                    Text("... e mais ${erros.size - 5}")
                                }
                            }
                        }
                    }
                }

                item {
                    Button(
                        onClick = { controller.validarESalvar() },
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)
                    ) {
                        Text(if (modoTreinamento) "Validar" else "Salvar")
                    }
                }

                item {
                    Text(
                        "Plataforma: ${platformName()}",
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            Column(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                FloatingActionButton(
                    onClick = { scrollSeguro(0) },
                    containerColor = MaterialTheme.colorScheme.primaryContainer
                ) {
                    Text("Início", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                }
                FloatingActionButton(
                    onClick = {
                        val last = (listState.layoutInfo.totalItemsCount - 1).coerceAtLeast(0)
                        scrollSeguro(last)
                    },
                    containerColor = MaterialTheme.colorScheme.primaryContainer
                ) {
                    Text("Fim", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall)
                }
            }
        }
    }

    if (embedded) {
        content(PaddingValues(0.dp))
    } else {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbar) },
            content = content
        )
    }

    if (mostrarListaInfoAdicionais) {
        val ultimaFoto = form.informacoesAdicionaisFotos.lastOrNull()
        CenteredAppDialog(
            onDismissRequest = { mostrarListaInfoAdicionais = false },
            title = "Informações adicionais — fotos",
            text = if (form.informacoesAdicionaisFotos.isEmpty()) {
                "Nenhuma foto."
            } else {
                "${form.informacoesAdicionaisFotos.size} foto(s). Remover a última?"
            },
            confirmLabel = if (ultimaFoto != null) "Remover última" else null,
            onConfirm = if (ultimaFoto != null) {
                {
                    controller.removerFotoInformacoesAdicionais(ultimaFoto)
                    if (form.informacoesAdicionaisFotos.size <= 1) mostrarListaInfoAdicionais = false
                }
            } else {
                null
            },
            dismissLabel = "Fechar"
        )
    }
}

@Composable
private fun SharedFormHeader(config: AppConfig) {
    var bitmap by remember(config.bannerPath) { mutableStateOf<ImageBitmap?>(null) }

    LaunchedEffect(config.bannerPath) {
        bitmap = loadFormBannerBitmap(config.bannerPath)
    }

    val bmp = bitmap
    if (bmp != null) {
        // Altura proporcional à largura (não 80.dp fixo): FillWidth + altura baixa
        // cortava o rodapé do topo_banner (texto verde / capacete) na web.
        val aspect = bmp.width.toFloat() / bmp.height.toFloat().coerceAtLeast(1f)
        Image(
            bitmap = bmp,
            contentDescription = config.nomeInspecao,
            modifier = Modifier
                .fillMaxWidth()
                .aspectRatio(aspect),
            contentScale = ContentScale.FillWidth
        )
    } else {
        // Fallback enquanto carrega ou se o asset não estiver disponível.
        Card(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.primaryContainer)
        ) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(config.nomeApp, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                Text(config.nomeInspecao, style = MaterialTheme.typography.titleMedium)
            }
        }
    }
}

private suspend fun loadFormBannerBitmap(bannerPath: String?): ImageBitmap? {
    return runCatching {
        val custom = bannerPath
            ?.takeIf { it.isNotBlank() }
            ?.let { PlatformServices.loadImageBytes(it) }
        val bytes = custom ?: PlatformServices.loadBundledAsset("topo_banner.jpg") ?: return null
        decodeToImageBitmap(bytes)
    }.getOrNull()
}

/**
 * Índice do LazyColumn para o campo com erro (mesma ordem dos `item { }` do formulário).
 */
private fun indiceLazyDoCampo(
    campo: String,
    modoTreinamento: Boolean,
    idEdicao: Long?,
    itemIds: List<String>
): Int {
    var i = 0
    i++ // banner header (sempre visível, como no APK)
    if (modoTreinamento) i++
    i++ // Ver manual / Nova
    i++ // NR Vigente
    if (idEdicao != null) i++
    val idxDados = i++
    val idxLocal = i++
    i++ // progress
    i++ // título verificações
    val idxPrimeiroItem = i
    i += itemIds.size
    val idxDesvios = i++
    val idxAcoes = i++
    val idxInfoAdicionais = i++
    val idxConclusao = i++
    val idxAssinaturas = i

    return when (campo) {
        "tstResponsavel", "efetivo", "obra" -> idxDados
        "localGps", "responsavelFrente", "descricaoAtividade" -> idxLocal
        "desvios" -> idxDesvios
        "acoesCorretivas" -> idxAcoes
        "informacoesAdicionais" -> idxInfoAdicionais
        "conclusao" -> idxConclusao
        "assinaturaSupervisor", "assinaturaSesmt" -> idxAssinaturas
        else -> {
            val pos = itemIds.indexOf(campo)
            if (pos >= 0) idxPrimeiroItem + pos else idxDados
        }
    }
}
